import { useState, useEffect, useCallback } from "react";
import { API_ENDPOINTS } from "@/constants/constants";
import {
  RefreshCw,
  Search,
  Edit,
  Trash2,
  X,
  PlusCircle,
  Calendar,
  Clock,
  Eye,
  PlayCircle,
} from "lucide-react";

/**
 * AdminLive Component
 *
 * This component allows administrators to manage live meetings/webinars.
 * The API integration follows the Zoom API format for creating and managing meetings:
 * - Uses 'topic' instead of 'title'
 * - Uses 'duration' (in minutes) rather than 'endTime'
 * - Includes 'settings' with options like waiting room, recording, etc.
 * - Supports user invitations via 'invitedUserIds'
 *
 * The form data is converted to the Zoom format before sending to the API.
 */

// Interface pour les réunions en direct
interface Meeting {
  id: number;
  topic: string;
  description: string;
  startTime: string;
  duration: number;
  password?: string;
  isRecurring: boolean;
  recurrenceType: string | null;
  zoomMeetingId: string;
  zoomJoinUrl: string;
  zoomStartUrl: string;
  status: string;
  timezone: string;
  settings: {
    audio: string;
    use_pmi: boolean;
    jbh_time: number;
    join_before_host: boolean;
    waiting_room: boolean;
    [key: string]: unknown;
  };
  createdBy: number;
  invitedUserIds: string[];
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string;
    role?: string;
  };
  invitedUsers: InvitedUser[];
}

// Interface pour un utilisateur tel que reçu de l'API
interface ApiUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  fonction?: string;
  service?: string;
  profileImage?: string;
  citation?: string;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
}

// Interface pour un utilisateur simplifié à utiliser dans le formulaire
interface InvitedUser {
  id: string;
  name: string;
  email: string;
  fonction?: string;
  service?: string;
}

// Interface pour le formulaire de réunion
interface MeetingForm {
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  invitedUsers: InvitedUser[];
  isRecurring: boolean;
  recurrenceType: string;
}

const AdminLive = () => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [emailSearch, setEmailSearch] = useState("");
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);

  // États pour le modal de création
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState<MeetingForm>({
    title: "",
    description: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    invitedUsers: [],
    isRecurring: false,
    recurrenceType: "weekly",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState(false);

  // États pour le modal de mise à jour
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [updateForm, setUpdateForm] = useState<MeetingForm>({
    title: "",
    description: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    invitedUsers: [],
    isRecurring: false,
    recurrenceType: "weekly",
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // État pour le processus de suppression
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  // État pour le modal de participation à une réunion
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [selectedJoinMeeting, setSelectedJoinMeeting] =
    useState<Meeting | null>(null);

  // Utilisation de useCallback pour optimiser les performances
  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(API_ENDPOINTS.MEETINGS.GET_ALL_MEETINGS, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Erreur lors de la récupération des diffusions en direct"
        );
      }

      console.log("Données des réunions reçues:", data);
      setMeetings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      console.error("Erreur de récupération des réunions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeetings();
    fetchUsers();

    // Mettre en place un système de rafraîchissement automatique toutes les minutes
    const intervalId = setInterval(() => {
      fetchMeetings();
    }, 60000); // Rafraîchir toutes les 60 secondes (1 minute)

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchMeetings]);

  // Fonction pour récupérer la liste des utilisateurs
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Utiliser l'endpoint défini dans les constantes pour récupérer les utilisateurs
      const response = await fetch(API_ENDPOINTS.AUTH.GET_USERS, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la récupération des utilisateurs"
        );
      }

      console.log("Données des utilisateurs reçues:", data);
      setUsers(data);
    } catch (err) {
      console.error("Erreur de récupération des utilisateurs:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Filtrer les réunions en fonction du terme de recherche
  const filteredMeetings = meetings.filter(
    (meeting) =>
      meeting.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
      meeting.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString;

      return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(date);
    } catch (error) {
      console.error("Erreur formatage date:", error);
      return dateString;
    }
  };

  const handleCreateInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setUpdateForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Fonction pour gérer la recherche d'email
  const handleEmailSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailSearch(value);

    if (value.length > 0) {
      setShowUserSuggestions(true);
    } else {
      setShowUserSuggestions(false);
    }
  };

  // Filtrer les utilisateurs en fonction de la recherche d'email
  const filteredUsersByEmail =
    emailSearch.length > 0
      ? users.filter(
          (user) =>
            user.email.toLowerCase().includes(emailSearch.toLowerCase()) ||
            (user.firstName + " " + user.lastName)
              .toLowerCase()
              .includes(emailSearch.toLowerCase())
        )
      : [];

  // Fonction pour sélectionner un utilisateur à partir des suggestions
  const handleSelectUserFromSuggestion = (user: ApiUser, isUpdate: boolean) => {
    const userForInvite: InvitedUser = {
      id: String(user.id),
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      fonction: user.fonction,
      service: user.service,
    };

    if (isUpdate) {
      // Pour le modal de mise à jour
      if (!updateForm.invitedUsers.some((u) => u.id === userForInvite.id)) {
        setUpdateForm((prev) => ({
          ...prev,
          invitedUsers: [...prev.invitedUsers, userForInvite],
        }));
      }
    } else {
      // Pour le modal de création
      if (!createForm.invitedUsers.some((u) => u.id === userForInvite.id)) {
        setCreateForm((prev) => ({
          ...prev,
          invitedUsers: [...prev.invitedUsers, userForInvite],
        }));
      }
    }

    // Réinitialiser la recherche
    setEmailSearch("");
    setShowUserSuggestions(false);
  };

  // Gérer la suppression d'un utilisateur invité
  const handleRemoveInvitedUser = (userId: string, isUpdate: boolean) => {
    if (isUpdate) {
      // Pour le modal de mise à jour
      setUpdateForm((prev) => ({
        ...prev,
        invitedUsers: prev.invitedUsers.filter((user) => user.id !== userId),
      }));
    } else {
      // Pour le modal de création
      setCreateForm((prev) => ({
        ...prev,
        invitedUsers: prev.invitedUsers.filter((user) => user.id !== userId),
      }));
    }
  };

  // Format data for Zoom API request
  const formatZoomMeetingData = (formData: MeetingForm) => {
    // Combiner les dates et heures en un seul timestamp ISO
    const getISODateTime = (date: string, time: string) => {
      if (!date || !time) return "";
      try {
        const dateTimeStr = `${date}T${time}:00`;
        const dateObj = new Date(dateTimeStr);
        return dateObj.toISOString();
      } catch (error) {
        console.error("Erreur de conversion date/heure:", error);
        return "";
      }
    };

    const formattedStartTime = getISODateTime(
      formData.startDate,
      formData.startTime
    );

    // Calculate endTime in minutes for duration
    let duration = 60; // Default 60 minutes
    if (
      formData.startDate &&
      formData.startTime &&
      formData.endDate &&
      formData.endTime
    ) {
      const start = new Date(`${formData.startDate}T${formData.startTime}:00`);
      const end = new Date(`${formData.endDate}T${formData.endTime}:00`);
      duration = Math.round((end.getTime() - start.getTime()) / (1000 * 60)); // Duration in minutes
    }

    // Extraire les IDs des utilisateurs invités
    const invitedUserIds = formData.invitedUsers.map((user) => user.id);

    // Format for Zoom API - matches the exact format in the API docs
    return {
      topic: formData.title,
      description: formData.description,
      startTime: formattedStartTime,
      duration: duration,
      timezone: "UTC",
      isRecurring: formData.isRecurring,
      recurrenceType: formData.isRecurring ? formData.recurrenceType : null,
      settings: {
        use_pmi: false,
        mute_upon_entry: false,
        auto_recording: "none",
        join_before_host: true,
        waiting_room: false,
      },
      invitedUserIds: invitedUserIds,
    };
  };

  const handleCreateMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError("");
    setCreateSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Format the data for Zoom API
      const zoomMeetingData = formatZoomMeetingData(createForm);

      const response = await fetch(API_ENDPOINTS.MEETINGS.CREATE_MEETING, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(zoomMeetingData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Erreur lors de la création de la diffusion"
        );
      }

      fetchMeetings();
      setCreateSuccess(true);

      // Réinitialiser le formulaire
      resetCreateForm();

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsCreateModalOpen(false);
        setCreateSuccess(false);
      }, 2000);
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur lors de la création de la réunion:", err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditMeeting = (meeting: Meeting) => {
    setSelectedMeeting(meeting);

    // Formater les dates pour les champs date et time
    const formatDateForInput = (dateString: string, isDate: boolean) => {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return "";

        if (isDate) {
          return date.toISOString().split("T")[0]; // Format YYYY-MM-DD
        } else {
          return date.toTimeString().substring(0, 5); // Format HH:MM
        }
      } catch (error) {
        console.error("Erreur formatage date pour input:", error);
        return "";
      }
    };

    // Calculer la date de fin à partir de la durée
    const calculateEndTime = (
      startTimeStr: string,
      durationMinutes: number
    ) => {
      if (!startTimeStr) return "";
      try {
        const startTime = new Date(startTimeStr);
        if (Number.isNaN(startTime.getTime())) return "";

        const endTime = new Date(startTime.getTime() + durationMinutes * 60000);
        return endTime.toISOString();
      } catch (error) {
        console.error("Erreur calcul date de fin:", error);
        return "";
      }
    };

    // Calculer la date/heure de fin basée sur la durée
    const endTime = calculateEndTime(meeting.startTime, meeting.duration);

    // Convertir les participants (qui sont des ID) en objets InvitedUser
    const invitedUsers: InvitedUser[] = (meeting.invitedUserIds || []).map(
      (participantId) => {
        // Trouver l'utilisateur correspondant dans la liste des utilisateurs
        const user = users.find((u) => String(u.id) === participantId);
        // Si trouvé, retourner l'objet formaté, sinon un objet minimal
        if (user) {
          return {
            id: String(user.id),
            name: `${user.firstName} ${user.lastName}`,
            email: user.email,
            fonction: user.fonction,
            service: user.service,
          };
        } else {
          return {
            id: participantId,
            name: `Utilisateur ${participantId}`,
            email: "",
          };
        }
      }
    );

    setUpdateForm({
      title: meeting.topic,
      description: meeting.description,
      startDate: formatDateForInput(meeting.startTime, true),
      startTime: formatDateForInput(meeting.startTime, false),
      endDate: formatDateForInput(endTime, true),
      endTime: formatDateForInput(endTime, false),
      invitedUsers,
      isRecurring: meeting.isRecurring,
      recurrenceType: meeting.recurrenceType || "weekly",
    });

    setIsUpdateModalOpen(true);
  };

  const handleUpdateMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMeeting) return;

    setUpdateLoading(true);
    setUpdateError("");
    setUpdateSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Format the data for Zoom API
      const zoomMeetingData = formatZoomMeetingData(updateForm);

      const response = await fetch(
        API_ENDPOINTS.MEETINGS.UPDATE_MEETING(String(selectedMeeting.id)),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(zoomMeetingData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Erreur lors de la mise à jour de la diffusion"
        );
      }

      fetchMeetings();
      setUpdateSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsUpdateModalOpen(false);
        setUpdateSuccess(false);
        setSelectedMeeting(null);
      }, 2000);
    } catch (err) {
      setUpdateError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur lors de la mise à jour de la réunion:", err);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteMeeting = async (meetingId: number) => {
    if (
      !confirm("Êtes-vous sûr de vouloir supprimer cette diffusion en direct ?")
    ) {
      return;
    }

    setDeleteLoading(String(meetingId));

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(
        API_ENDPOINTS.MEETINGS.DELETE_MEETING(String(meetingId)),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Erreur lors de la suppression de la diffusion"
        );
      }

      // Mettre à jour la liste des réunions après la suppression
      fetchMeetings();
    } catch (err) {
      console.error("Erreur lors de la suppression de la réunion:", err);
      alert(err instanceof Error ? err.message : "Une erreur est survenue");
    } finally {
      setDeleteLoading(null);
    }
  };

  // Réinitialiser le formulaire
  const resetCreateForm = () => {
    setCreateForm({
      title: "",
      description: "",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      invitedUsers: [],
      isRecurring: false,
      recurrenceType: "weekly",
    });
  };

  // Fonction pour ouvrir le modal de participation à une réunion
  const handleJoinMeeting = (meeting: Meeting) => {
    setSelectedJoinMeeting(meeting);
    setIsJoinModalOpen(true);
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">Live/Direct</h1>
      <p className="mt-4">Gérez les diffusions en direct de la plateforme.</p>
      <div className="mt-8 bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">
            Programmation des diffusions
          </h2>
          <div className="flex gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher..."
                className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search
                className="absolute left-3 top-2.5 text-gray-400"
                size={18}
              />
            </div>
            <button
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
              onClick={fetchMeetings}
              disabled={loading}
            >
              <RefreshCw
                size={18}
                className={`text-gray-600 ${loading ? "animate-spin" : ""}`}
              />
              <span>Actualiser</span>
            </button>
            <button
              className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <PlusCircle size={18} />
              <span>Créer une diffusion</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <RefreshCw size={24} className="animate-spin text-blue-500" />
            <span className="ml-2">Chargement des diffusions...</span>
          </div>
        ) : filteredMeetings.length === 0 ? (
          <p className="text-center py-8 text-gray-500">
            {searchTerm
              ? "Aucune diffusion ne correspond à votre recherche"
              : "Aucune diffusion prévue pour le moment."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Titre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Début
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredMeetings.map((meeting, index) => (
                  <tr key={meeting.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-500">
                        {index + 1}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {meeting.topic}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {formatDateTime(meeting.startTime)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {meeting.duration} minutes
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className={`text-sm font-medium px-2 py-1 rounded-full text-center ${
                          meeting.status === "scheduled"
                            ? "bg-blue-100 text-blue-800"
                            : meeting.status === "in_progress"
                            ? "bg-green-100 text-green-800"
                            : meeting.status === "ended"
                            ? "bg-gray-100 text-gray-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {meeting.status === "scheduled"
                          ? "Programmée"
                          : meeting.status === "in_progress"
                          ? "En cours"
                          : meeting.status === "ended"
                          ? "Terminée"
                          : meeting.status}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {(meeting.status === "scheduled" ||
                          meeting.status === "in_progress") && (
                          <button
                            onClick={() => handleJoinMeeting(meeting)}
                            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded"
                            title="Rejoindre"
                          >
                            <Eye size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleEditMeeting(meeting)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                          title="Modifier"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteMeeting(Number(meeting.id))
                          }
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                          title="Supprimer"
                          disabled={deleteLoading === String(meeting.id)}
                        >
                          {deleteLoading === String(meeting.id) ? (
                            <div className="animate-spin h-4 w-4 border-2 border-red-500 rounded-full border-t-transparent"></div>
                          ) : (
                            <Trash2 size={18} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de création de diffusion */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-2xl relative">
            <button
              onClick={() => setIsCreateModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">
              Créer une nouvelle diffusion
            </h2>

            {createSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Diffusion créée avec succès!</p>
              </div>
            )}

            {createError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{createError}</p>
              </div>
            )}

            <form onSubmit={handleCreateMeeting}>
              <div className="mb-4">
                <label htmlFor="create-title" className="block text-gray-700 text-sm font-bold mb-2">
                  Titre
                </label>
                <input
                  id="create-title"
                  type="text"
                  name="title"
                  value={createForm.title}
                  onChange={handleCreateInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="create-description" className="block text-gray-700 text-sm font-bold mb-2">
                  Description
                </label>
                <textarea
                  id="create-description"
                  name="description"
                  value={createForm.description}
                  onChange={handleCreateInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="create-start-date" className="block text-gray-700 text-sm font-bold mb-2">
                    Date de début
                  </label>
                  <div className="relative">
                    <input
                      id="create-start-date"
                      type="date"
                      name="startDate"
                      value={createForm.startDate}
                      onChange={handleCreateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
                      required
                    />
                    <Calendar
                      className="absolute left-3 top-2.5 text-gray-400"
                      size={18}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="create-start-time" className="block text-gray-700 text-sm font-bold mb-2">
                    Heure de début
                  </label>
                  <div className="relative">
                    <input
                      id="create-start-time"
                      type="time"
                      name="startTime"
                      value={createForm.startTime}
                      onChange={handleCreateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
                      required
                    />
                    <Clock
                      className="absolute left-3 top-2.5 text-gray-400"
                      size={18}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="create-end-date" className="block text-gray-700 text-sm font-bold mb-2">
                    Date de fin
                  </label>
                  <div className="relative">
                    <input
                      id="create-end-date"
                      type="date"
                      name="endDate"
                      value={createForm.endDate}
                      onChange={handleCreateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
                      required
                    />
                    <Calendar
                      className="absolute left-3 top-2.5 text-gray-400"
                      size={18}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="create-end-time" className="block text-gray-700 text-sm font-bold mb-2">
                    Heure de fin
                  </label>
                  <div className="relative">
                    <input
                      id="create-end-time"
                      type="time"
                      name="endTime"
                      value={createForm.endTime}
                      onChange={handleCreateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
                      required
                    />
                    <Clock
                      className="absolute left-3 top-2.5 text-gray-400"
                      size={18}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="isRecurring"
                    name="isRecurring"
                    checked={createForm.isRecurring}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        isRecurring: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="isRecurring"
                    className="ml-2 block text-gray-700 text-sm font-bold"
                  >
                    Réunion récurrente
                  </label>
                </div>

                {createForm.isRecurring && (
                  <div className="mt-2 pl-6">
                    <label htmlFor="create-recurrence-type" className="block text-gray-700 text-sm font-bold mb-2">
                      Type de récurrence
                    </label>
                    <select
                      id="create-recurrence-type"
                      name="recurrenceType"
                      value={createForm.recurrenceType}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          recurrenceType: e.target.value,
                        }))
                      }
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="daily">Quotidienne</option>
                      <option value="weekly">Hebdomadaire</option>
                      <option value="monthly">Mensuelle</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="create-email-search" className="block text-gray-700 text-sm font-bold mb-2">
                  Inviter des utilisateurs
                </label>
                <div className="space-y-3">
                  {loadingUsers ? (
                    <div className="flex items-center justify-center p-4">
                      <RefreshCw className="animate-spin h-5 w-5 mr-2 text-blue-500" />
                      <span className="text-sm text-gray-500">
                        Chargement des utilisateurs...
                      </span>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        id="create-email-search"
                        type="text"
                        className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Rechercher par email ou nom..."
                        value={emailSearch}
                        onChange={handleEmailSearch}
                      />
                      {showUserSuggestions &&
                        filteredUsersByEmail.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredUsersByEmail.map((user) => (
                              <div
                                key={user.id}
                                className="p-2 hover:bg-blue-50 cursor-pointer flex items-center justify-between"
                                onClick={() =>
                                  handleSelectUserFromSuggestion(user, false)
                                }
                              >
                                <div>
                                  <div className="font-medium">
                                    {user.firstName} {user.lastName}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {user.email}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  )}

                  {createForm.invitedUsers.length > 0 ? (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Utilisateurs invités:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {createForm.invitedUsers.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm"
                          >
                            <span>{user.name}</span>
                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveInvitedUser(user.id, false)
                              }
                              className="ml-1 text-blue-600 hover:text-blue-800"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      Aucun utilisateur invité pour le moment
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-2"
                  disabled={createLoading}
                >
                  {createLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de mise à jour d'une diffusion */}
      {isUpdateModalOpen && selectedMeeting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-2xl relative">
            <button
              onClick={() => setIsUpdateModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Modifier la diffusion</h2>

            {updateSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Diffusion modifiée avec succès!</p>
              </div>
            )}

            {updateError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{updateError}</p>
              </div>
            )}

            <form onSubmit={handleUpdateMeeting}>
              <div className="mb-4">
                <label htmlFor="update-title" className="block text-gray-700 text-sm font-bold mb-2">
                  Titre
                </label>
                <input
                  id="update-title"
                  type="text"
                  name="title"
                  value={updateForm.title}
                  onChange={handleUpdateInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="update-description" className="block text-gray-700 text-sm font-bold mb-2">
                  Description
                </label>
                <textarea
                  id="update-description"
                  name="description"
                  value={updateForm.description}
                  onChange={handleUpdateInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="update-start-date" className="block text-gray-700 text-sm font-bold mb-2">
                    Date de début
                  </label>
                  <div className="relative">
                    <input
                      id="update-start-date"
                      type="date"
                      name="startDate"
                      value={updateForm.startDate}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
                      required
                    />
                    <Calendar
                      className="absolute left-3 top-2.5 text-gray-400"
                      size={18}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="update-start-time" className="block text-gray-700 text-sm font-bold mb-2">
                    Heure de début
                  </label>
                  <div className="relative">
                    <input
                      id="update-start-time"
                      type="time"
                      name="startTime"
                      value={updateForm.startTime}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
                      required
                    />
                    <Clock
                      className="absolute left-3 top-2.5 text-gray-400"
                      size={18}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="update-end-date" className="block text-gray-700 text-sm font-bold mb-2">
                    Date de fin
                  </label>
                  <div className="relative">
                    <input
                      id="update-end-date"
                      type="date"
                      name="endDate"
                      value={updateForm.endDate}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
                      required
                    />
                    <Calendar
                      className="absolute left-3 top-2.5 text-gray-400"
                      size={18}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="update-end-time" className="block text-gray-700 text-sm font-bold mb-2">
                    Heure de fin
                  </label>
                  <div className="relative">
                    <input
                      id="update-end-time"
                      type="time"
                      name="endTime"
                      value={updateForm.endTime}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
                      required
                    />
                    <Clock
                      className="absolute left-3 top-2.5 text-gray-400"
                      size={18}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="updateIsRecurring"
                    name="isRecurring"
                    checked={updateForm.isRecurring}
                    onChange={(e) =>
                      setUpdateForm((prev) => ({
                        ...prev,
                        isRecurring: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="updateIsRecurring"
                    className="ml-2 block text-gray-700 text-sm font-bold"
                  >
                    Réunion récurrente
                  </label>
                </div>

                {updateForm.isRecurring && (
                  <div className="mt-2 pl-6">
                    <label htmlFor="update-recurrence-type" className="block text-gray-700 text-sm font-bold mb-2">
                      Type de récurrence
                    </label>
                    <select
                      id="update-recurrence-type"
                      name="recurrenceType"
                      value={updateForm.recurrenceType}
                      onChange={(e) =>
                        setUpdateForm((prev) => ({
                          ...prev,
                          recurrenceType: e.target.value,
                        }))
                      }
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="daily">Quotidienne</option>
                      <option value="weekly">Hebdomadaire</option>
                      <option value="monthly">Mensuelle</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="update-email-search" className="block text-gray-700 text-sm font-bold mb-2">
                  Inviter des utilisateurs
                </label>
                <div className="space-y-3">
                  {loadingUsers ? (
                    <div className="flex items-center justify-center p-4">
                      <RefreshCw className="animate-spin h-5 w-5 mr-2 text-blue-500" />
                      <span className="text-sm text-gray-500">
                        Chargement des utilisateurs...
                      </span>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        id="update-email-search"
                        type="text"
                        className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Rechercher par email ou nom..."
                        value={emailSearch}
                        onChange={handleEmailSearch}
                      />
                      {showUserSuggestions &&
                        filteredUsersByEmail.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredUsersByEmail.map((user) => (
                              <div
                                key={user.id}
                                className="p-2 hover:bg-blue-50 cursor-pointer flex items-center justify-between"
                                onClick={() =>
                                  handleSelectUserFromSuggestion(user, true)
                                }
                              >
                                <div>
                                  <div className="font-medium">
                                    {user.firstName} {user.lastName}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {user.email}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  )}

                  {updateForm.invitedUsers.length > 0 ? (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Utilisateurs invités:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {updateForm.invitedUsers.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm"
                          >
                            <span>{user.name}</span>
                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveInvitedUser(user.id, true)
                              }
                              className="ml-1 text-blue-600 hover:text-blue-800"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      Aucun utilisateur invité pour le moment
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsUpdateModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
                  disabled={updateLoading}
                >
                  {updateLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Mettre à jour
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de participation à une réunion */}
      {isJoinModalOpen && selectedJoinMeeting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden w-full max-w-[95vw] h-[95vh] flex flex-col">
            {/* En-tête */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-xl font-bold text-white">
                    {selectedJoinMeeting.topic}
                  </h1>
                  <p className="text-gray-300 text-sm mt-1">
                    {selectedJoinMeeting.description}
                  </p>
                </div>
                <button
                  onClick={() => setIsJoinModalOpen(false)}
                  className="text-white hover:text-gray-300"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
              {/* Zone de la réunion */}
              <div className="flex-1 relative min-h-[50vh] md:min-h-0">
                <div className="absolute inset-0 bg-transparent z-10 pointer-events-none"></div>
                <iframe
                  src={`https://zoom.us/wc/join/${
                    selectedJoinMeeting.zoomMeetingId
                  }?pwd=${encodeURIComponent(
                    selectedJoinMeeting.password || ""
                  )}&webinar=false&audio=true&video=true&web=true&meeting_result=false&waiting_room=false&jbh=true&auto_join=true`}
                  className="w-full h-full border-0"
                  allow="camera *; microphone *; fullscreen; display-capture; autoplay; clipboard-read; clipboard-write; web-share; encrypted-media; geolocation"
                ></iframe>

                {/* Overlay avec instructions et boutons */}
                <div
                  className="absolute top-0 left-0 right-0 bg-black bg-opacity-70 text-white p-2 text-xs md:text-sm flex justify-between items-center"
                  style={{ zIndex: 100 }}
                >
                  <div className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1 text-yellow-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      Pour une meilleure expérience, utilisez l'application Zoom
                    </span>
                  </div>
                  <button
                    onClick={() =>
                      window.open(
                        `zoommtg://zoom.us/join?action=join&confno=${
                          selectedJoinMeeting.zoomMeetingId
                        }&pwd=${
                          selectedJoinMeeting.password || ""
                        }&zc=0&browser=chrome&waiting_room=false&jbh=true&auto_join=true`,
                        "_blank"
                      )
                    }
                    className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs md:text-sm flex items-center"
                  >
                    <PlayCircle size={14} className="mr-1" />
                    Ouvrir dans Zoom
                  </button>
                </div>
              </div>

              {/* Informations de la réunion */}
              <div className="w-full md:w-80 bg-gray-50 overflow-y-auto p-4">
                <div className="space-y-4">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <h2 className="text-lg font-semibold mb-3">
                      Informations de la réunion
                    </h2>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <Calendar className="text-gray-500 mr-2" size={18} />
                        <span className="text-sm">
                          {formatDateTime(selectedJoinMeeting.startTime)}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="text-gray-500 mr-2" size={18} />
                        <span className="text-sm">
                          {selectedJoinMeeting.duration} minutes
                        </span>
                      </div>
                    </div>
                  </div>

                  {selectedJoinMeeting.password && (
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">
                        Informations de connexion
                      </h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-gray-500">
                            ID de réunion:
                          </span>
                          <p className="font-mono text-sm">
                            {selectedJoinMeeting.zoomMeetingId}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">
                            Mot de passe:
                          </span>
                          <p className="font-mono text-sm">
                            {selectedJoinMeeting.password}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Informations de l'organisateur */}
                  {selectedJoinMeeting.creator && (
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                      <h2 className="text-lg font-semibold mb-3">
                        Organisateur
                      </h2>
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full overflow-hidden">
                          <img
                            src={
                              selectedJoinMeeting.creator.profileImage
                                ? `${import.meta.env.VITE_API_URL}/${
                                    selectedJoinMeeting.creator.profileImage
                                  }`
                                : "/images/avatar-placeholder.png"
                            }
                            alt={`${selectedJoinMeeting.creator.firstName} ${selectedJoinMeeting.creator.lastName}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {selectedJoinMeeting.creator.firstName}{" "}
                            {selectedJoinMeeting.creator.lastName}
                          </p>
                          {selectedJoinMeeting.creator.email && (
                            <p className="text-gray-500 text-xs">
                              {selectedJoinMeeting.creator.email}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLive;
